import { Form, FormProps, FormRenderProps } from 'components/form'
import { resolveValue } from 'components/form/final-form/hooks/useField'
import { useFlags } from 'config/useFlagsContext'
import { FormApi } from 'final-form'
import createCalculator from 'final-form-calculate'
import { MedicamentoCatmat } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import React, { useCallback, useMemo, useRef } from 'react'
import { metaPath } from 'util/metaPath'
import { MedicaoAnteriorFormModel } from 'view/atendimentos/detail/components/modals/medicoes/MedicaoAnteriorForm'

import { MedicamentoFormModel, TipoMotivoPrescricaoEnum } from '../model-prescricao'
import { createDefaultMedicamento } from '../utils/utils-prescricao'
import { prescricaoFormValidator as validator } from '../utils/validator-prescricao'
import AdicionarMedicamentoFormPanel from './AdicionarMedicamentoFormPanel'

export interface PrescricaoMedicamentoFormProps extends FormProps<MedicamentoFormModel> {
  isReplicar: boolean
  medicamentoToEdit: MedicamentoFormModel
  pesoAtual?: string
  pesoAoNascer?: string
  medicoesAnteriores?: MedicaoAnteriorFormModel[]
  onClearEditedMedicamento(newEditedValue: MedicamentoFormModel): void
  onVerifyMedicamentoDuplicated(medicamento: MedicamentoFormModel): boolean
  scrollModal(): void
  showDoseAlert: boolean
}

const path = metaPath<MedicamentoFormModel>()

const unidadeMedidaCalculator = createCalculator({
  field: path.principioAtivoCombo.absolutePath(),
  updates: {
    [path.unidadeMedidaDose.absolutePath()]: (medicamento: MedicamentoCatmat, values: MedicamentoFormModel) => {
      const unidadeMedidaDose = resolveValue(values, path.unidadeMedidaDose)
      const isEditFirstLoad = resolveValue(values, path.isEditFirstLoad)
      const isRegistroManual = resolveValue(values, path.registroManual)
      if (!isRegistroManual) {
        if (isEditFirstLoad) {
          return unidadeMedidaDose
        } else {
          if (medicamento) {
            if (!!medicamento.unidadeMedidaDose && unidadeMedidaDose !== medicamento.unidadeMedidaDose) {
              return medicamento.unidadeMedidaDose
            } else {
              return unidadeMedidaDose
            }
          } else {
            return null
          }
        }
      } else {
        return unidadeMedidaDose
      }
    },
    [path.qtDose.absolutePath()]: (medicamento: MedicamentoCatmat, values: MedicamentoFormModel) => {
      const qtDose = resolveValue(values, path.qtDose)
      const isRegistroManual = resolveValue(values, path.registroManual)
      if (!isRegistroManual) {
        if (medicamento) {
          return qtDose
        } else {
          return null
        }
      } else {
        return qtDose
      }
    },
    [path.isEditFirstLoad.absolutePath()]: () => {
      return false
    },
  },
})

export default function PrescricaoMedicamentoForm(props: PrescricaoMedicamentoFormProps) {
  const {
    onSubmit,
    isReplicar,
    medicamentoToEdit,
    pesoAtual,
    pesoAoNascer,
    medicoesAnteriores,
    onClearEditedMedicamento,
    onVerifyMedicamentoDuplicated,
    scrollModal,
    showDoseAlert,
  } = props

  const {
    atendimentoProfissional: { iniciadoEm: dataAtendimento },
    permissoes,
  } = useAtendimentoContext()

  const { MOTIVO_PRESCRICAO_ENABLED } = useFlags()
  const podeRegistrarCid = !permissoes.somenteCiap

  const initialOnMountRef = useRef<MedicamentoFormModel>()
  if (!initialOnMountRef.current && dataAtendimento) {
    initialOnMountRef.current = createDefaultMedicamento(dataAtendimento, {
      isMotivoPrescricaoEnabled: MOTIVO_PRESCRICAO_ENABLED,
      podeRegistrarCid,
    })
  }

  const sanitizedInitialValues = useMemo(() => {
    if (!medicamentoToEdit) return initialOnMountRef.current

    const values = { ...medicamentoToEdit }
    const isRenovacao = !!values.receitaUsoContinuoGrupoId
    const isRenovacaoOuReplicacao = isRenovacao || isReplicar

    if (isRenovacaoOuReplicacao && MOTIVO_PRESCRICAO_ENABLED) {
      const { tipoMotivoPrescricao, cidMotivoPrescricao } = values

      if (!tipoMotivoPrescricao) {
        values.tipoMotivoPrescricao = podeRegistrarCid ? TipoMotivoPrescricaoEnum.CID10 : TipoMotivoPrescricaoEnum.CIAP2
      } else if (tipoMotivoPrescricao === TipoMotivoPrescricaoEnum.CID10 && !podeRegistrarCid) {
        values.tipoMotivoPrescricao = TipoMotivoPrescricaoEnum.CIAP2
        values.cidMotivoPrescricao = null
        values.ciapMotivoPrescricao = null
      } else if (tipoMotivoPrescricao === TipoMotivoPrescricaoEnum.CID10 && cidMotivoPrescricao?.ativo === false) {
        values.cidMotivoPrescricao = null
      }
    }
    return values
  }, [medicamentoToEdit, isReplicar, MOTIVO_PRESCRICAO_ENABLED, podeRegistrarCid])

  const handleOnSubmit = useCallback(
    (medicamentoToAdd: MedicamentoFormModel, formApi: FormApi) => {
      onSubmit(medicamentoToAdd, formApi)
      formApi.getRegisteredFields().forEach((field) => formApi.resetFieldState(field))
      const nextDefault = createDefaultMedicamento(dataAtendimento, {
        isMotivoPrescricaoEnabled: MOTIVO_PRESCRICAO_ENABLED,
        podeRegistrarCid,
      })
      initialOnMountRef.current = nextDefault
      formApi.initialize(nextDefault)
      setTimeout(formApi.reset)
      onClearEditedMedicamento(null)
    },
    [MOTIVO_PRESCRICAO_ENABLED, dataAtendimento, onClearEditedMedicamento, onSubmit, podeRegistrarCid]
  )

  const render = useCallback(
    (renderProps: FormRenderProps<MedicamentoFormModel>) => {
      return (
        <AdicionarMedicamentoFormPanel
          medicamentoToEdit={medicamentoToEdit}
          pesoAtual={pesoAtual}
          pesoAoNascer={pesoAoNascer}
          medicoesAnteriores={medicoesAnteriores}
          onClearEditedMedicamento={onClearEditedMedicamento}
          isEdit={!!medicamentoToEdit}
          isReplicar={isReplicar}
          onVerifyMedicamentoDuplicated={onVerifyMedicamentoDuplicated}
          showDoseAlert={showDoseAlert}
          {...renderProps}
        />
      )
    },
    [
      medicamentoToEdit,
      pesoAtual,
      pesoAoNascer,
      medicoesAnteriores,
      onClearEditedMedicamento,
      isReplicar,
      onVerifyMedicamentoDuplicated,
      showDoseAlert,
    ]
  )

  return useMemo(
    () => (
      <Form<MedicamentoFormModel>
        render={render}
        initialValues={sanitizedInitialValues}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value)
          },
        }}
        decorators={[unidadeMedidaCalculator]}
        validate={validator(dataAtendimento, MOTIVO_PRESCRICAO_ENABLED)}
        onSubmit={handleOnSubmit}
        onSubmitSucceeded={scrollModal}
        onSubmitFailed={scrollModal}
      />
    ),
    [render, sanitizedInitialValues, dataAtendimento, MOTIVO_PRESCRICAO_ENABLED, handleOnSubmit, scrollModal]
  )
}
